import { z } from 'zod';
import { IsoDateTime, Uuid } from './common';

export const MediaType = z.enum([
  'product',
  'lifestyle',
  'detail',
  'timelapse',
  'making_of',
  'packaging',
  'customer',
]);
export type MediaType = z.infer<typeof MediaType>;
export const MEDIA_TYPE_LABELS: Record<MediaType, string> = {
  product: 'Produto',
  lifestyle: 'Ambiente',
  detail: 'Detalhe',
  timelapse: 'Timelapse',
  making_of: 'Making-of',
  packaging: 'Embalagem',
  customer: 'Cliente',
};

export const MediaOrientation = z.enum(['vertical', 'feed', 'square', 'horizontal']);
export type MediaOrientation = z.infer<typeof MediaOrientation>;
export const ORIENTATION_LABELS: Record<MediaOrientation, string> = {
  vertical: 'Vertical 9:16',
  feed: 'Feed 4:5',
  square: 'Quadrado 1:1',
  horizontal: 'Horizontal 16:9',
};

/** Orientation from the displayed size (after EXIF rotation). */
export function orientationOf(width: number, height: number): MediaOrientation {
  const r = width / height;
  if (r < 0.7) return 'vertical';
  if (r < 0.9) return 'feed';
  if (r <= 1.12) return 'square';
  return 'horizontal';
}

export const PostPlace = z.enum([
  'ig_feed',
  'ig_reels',
  'ig_stories',
  'tiktok',
  'ml_listing',
  'shopee_listing',
  'whatsapp_status',
]);
export type PostPlace = z.infer<typeof PostPlace>;
export const POST_PLACE_LABELS: Record<PostPlace, string> = {
  ig_feed: 'Instagram · Feed',
  ig_reels: 'Instagram · Reels',
  ig_stories: 'Instagram · Stories',
  tiktok: 'TikTok',
  ml_listing: 'Mercado Livre · anúncio',
  shopee_listing: 'Shopee · anúncio',
  whatsapp_status: 'WhatsApp · status',
};

export const MediaStatus = z
  .enum(['queued', 'processing', 'ready', 'failed'])
  .describe(
    'Photos are resized and HEIC converted; videos get a poster frame and a compressed preview.',
  );

export const MediaPost = z.object({
  id: Uuid,
  where: PostPlace,
  postedOn: z.iso.date(),
  url: z.string().nullable(),
});
export type MediaPost = z.infer<typeof MediaPost>;

export const MediaItem = z
  .object({
    id: Uuid,
    product: z.object({ id: Uuid, name: z.string(), sku: z.string() }),
    variant: z.object({ id: Uuid, name: z.string() }).nullable(),
    type: MediaType,
    kind: z.enum(['photo', 'video']),
    status: MediaStatus,
    error: z.string().nullable(),
    sort: z.number().int(),
    isProductCover: z.boolean(),
    isVariantCover: z.boolean(),
    name: z.string().describe('Original file name.'),
    width: z.number().int().nullable(),
    height: z.number().int().nullable(),
    orientation: MediaOrientation.nullable(),
    durationSeconds: z.number().nullable(),
    capturedAt: IsoDateTime.nullable(),
    originalBytes: z.number().int(),
    previewBytes: z.number().int().nullable(),
    originalPruned: z
      .boolean()
      .describe('Only the compressed preview is kept ("guardar só a prévia").'),
    convertedFrom: z
      .string()
      .nullable()
      .describe('E.g. "HEIC" when the photo was converted to JPG.'),
    thumbUrl: z.string().nullable(),
    displayUrl: z
      .string()
      .nullable()
      .describe('Photo resized for the screen, or the video poster.'),
    videoUrl: z
      .string()
      .nullable()
      .describe('Compressed preview (or the original while it is processed).'),
    originalUrl: z.string().nullable(),
    approvedSocial: z.boolean(),
    approvedMarketplace: z.boolean(),
    customerConsent: z.enum(['not_needed', 'pending', 'granted']),
    printJobRef: z
      .string()
      .nullable()
      .describe('The print job it recorded (filled by the print queue later).'),
    note: z.string().nullable(),
    posts: z.array(MediaPost),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'MediaItem' });
export type MediaItem = z.infer<typeof MediaItem>;

export const AddMediaBody = z
  .object({
    fileId: Uuid.describe('A media file uploaded with the resumable `/uploads` (kind `media`).'),
    type: MediaType,
    variantId: Uuid.nullable().default(null),
    note: z.string().trim().max(500).nullable().default(null),
  })
  .meta({ id: 'AddMediaBody' });

export const UpdateMediaBody = z.object({
  type: MediaType.optional(),
  variantId: Uuid.nullable().optional(),
  approvedSocial: z.boolean().optional(),
  approvedMarketplace: z.boolean().optional(),
  customerConsent: z.enum(['not_needed', 'pending', 'granted']).optional(),
  note: z.string().trim().max(500).nullable().optional(),
});

export const MoveMediaBody = z
  .object({
    direction: z.enum(['up', 'down']).optional().describe('One step (↑ / ↓).'),
    position: z.number().int().min(0).optional().describe('0-based place (drag and drop).'),
  })
  .refine((b) => (b.direction === undefined) !== (b.position === undefined), {
    message: 'Send direction or position',
  });
export const MediaCoverBody = z.object({
  scope: z.enum(['product', 'variant']).default('product'),
});
export const AddPostBody = z.object({
  where: PostPlace,
  postedOn: z.iso.date(),
  url: z.url().nullable().default(null),
});

export const MediaList = z.object({
  data: z.array(MediaItem),
  nextCursor: z.string().nullable(),
  totals: z.object({
    count: z.number().int(),
    bytes: z.number().int().describe('Originals + previews of the filtered items.'),
    videoBytes: z.number().int(),
  }),
});
export const MediaQuery = z.object({
  productId: Uuid.optional(),
  variantId: Uuid.optional(),
  type: MediaType.optional(),
  kind: z.enum(['photo', 'video']).optional(),
  orientation: MediaOrientation.optional(),
  notPosted: z.enum(['true', 'false']).optional().describe('Only items never posted.'),
  approved: z.enum(['social', 'marketplace']).optional(),
  sort: z.enum(['gallery', 'recent']).default('recent').describe('`gallery` = product order.'),
});
